import React from 'react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  PieChart,
  Pie,
  RadarChart,
  Radar,
  RadialBarChart,
  RadialBar,
  Sankey,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts'
import { SideEffectFieldsFragment } from '../../../graphql'

// Base interfaces for chart data
interface BaseDataPoint {
  category: string
  value: number
}

interface ComposedDataPoint {
  category: string
  value1: number
  value2: number
}

interface PieDataPoint {
  name: string
  value: number
}

interface RadarDataPoint {
  subject: string
  score: number
}

interface SankeyNode {
  name: string
}

interface SankeyLink {
  source: number
  target: number
  value: number
}

// Chart configuration interfaces
interface BaseChartConfig {
  showLegend?: boolean
  color?: string
  seriesName?: string
}

interface ComposedChartConfig extends BaseChartConfig {
  data: ComposedDataPoint[]
  xKey: string
  series: Array<{
    type: 'bar' | 'line' | 'area'
    yKey: string
    seriesName?: string
    color?: string
  }>
}

interface PieChartConfig extends BaseChartConfig {
  data: PieDataPoint[]
  nameKey: string
  valueKey: string
  innerRadius?: number
  outerRadius?: number
}

interface RadarChartConfig extends BaseChartConfig {
  data: RadarDataPoint[]
  xKey: string
  yKey: string
}

interface RadialBarChartConfig extends BaseChartConfig {
  data: PieDataPoint[]
  nameKey: string
  valueKey: string
  innerRadius?: number
  outerRadius?: number
}

interface SankeyChartConfig extends BaseChartConfig {
  nodes: SankeyNode[]
  links: SankeyLink[]
}

const transformChartData = (data: BaseDataPoint[]) => {
  return data.map((item) => {
    // Try to parse as date, if it fails, use the original category
    try {
      const date = new Date(item.category)
      // Check if the date is valid
      if (!isNaN(date.getTime())) {
        return {
          ...item,
          formattedDate: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
        }
      }
    } catch (e) {
      // If date parsing fails, do nothing and fall through to default
    }
    // For non-date categories, use the original value
    return {
      ...item,
      formattedDate: item.category,
    }
  })
}

const RechartsChart: React.FC<{ config: SideEffectFieldsFragment['data'] }> = ({ config }) => {
  const { chartType, chartConfig } = config

  if (!chartConfig || !chartConfig.data || chartConfig.data.length === 0) {
    return <div>No data available</div>
  }

  // Common configurations
  const margin = { top: 20, right: 30, bottom: 50, left: 50 }
  const showLegend = chartConfig.showLegend !== false

  // Common components for cartesian charts
  const commonCartesianComponents = (
    <>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="formattedDate" angle={-45} height={60} textAnchor="end" interval={0} />
      <YAxis tickFormatter={(value) => value.toLocaleString()} width={80} />
      <Tooltip
        formatter={(value: any) => value.toLocaleString()}
        labelFormatter={(label) => `Date: ${label}`}
      />
      {showLegend && <Legend verticalAlign="top" height={36} />}
    </>
  )

  let chart = <></>

  switch (chartType) {
    case 'area': {
      const transformedAreaData = transformChartData(chartConfig.data)
      chart = (
        <AreaChart data={transformedAreaData} margin={margin}>
          {commonCartesianComponents}
          <Area
            type="monotone"
            dataKey="value"
            name={chartConfig.seriesName}
            stroke={chartConfig.color}
            fill={chartConfig.color}
            fillOpacity={0.6}
          />
        </AreaChart>
      )
      break
    }

    case 'bar': {
      const transformedBarData = transformChartData(chartConfig.data)
      chart = (
        <BarChart data={transformedBarData} margin={margin}>
          {commonCartesianComponents}
          <Bar
            dataKey="value"
            name={chartConfig.seriesName}
            fill={chartConfig.color}
            barSize={chartConfig.barSize}
          />
        </BarChart>
      )
      break
    }

    case 'line': {
      const transformedLineData = transformChartData(chartConfig.data)
      chart = (
        <LineChart data={transformedLineData} margin={margin}>
          {commonCartesianComponents}
          <Line
            type="monotone"
            dataKey="value"
            name={chartConfig.seriesName}
            stroke={chartConfig.color}
            strokeWidth={chartConfig.strokeWidth}
            dot={{ r: 4 }}
          />
        </LineChart>
      )
      break
    }

    case 'composed': {
      const composedConfig = chartConfig as unknown as ComposedChartConfig
      chart = (
        <ComposedChart data={composedConfig.data} margin={margin}>
          {commonCartesianComponents}
          {composedConfig.series.map((series, index) => {
            const commonProps = {
              key: index,
              dataKey: series.yKey,
              name: series.seriesName,
              stroke: series.color,
              fill: series.color,
            }

            switch (series.type) {
              case 'bar':
                return <Bar {...commonProps} />
              case 'line':
                return <Line {...commonProps} type="monotone" />
              case 'area':
                return <Area {...commonProps} type="monotone" fillOpacity={0.6} />
              default:
                return null
            }
          })}
        </ComposedChart>
      )
      break
    }

    case 'pie': {
      const pieConfig = chartConfig as unknown as PieChartConfig
      chart = (
        <PieChart margin={margin}>
          <Pie
            data={pieConfig.data}
            nameKey={pieConfig.nameKey}
            dataKey={pieConfig.valueKey}
            innerRadius={pieConfig.innerRadius}
            outerRadius={pieConfig.outerRadius}
            fill={pieConfig.color || '#8884d8'}
            label
          />
          <Tooltip formatter={(value: any) => value.toLocaleString()} />
          {showLegend && <Legend />}
        </PieChart>
      )
      break
    }

    case 'radar': {
      const radarConfig = chartConfig as unknown as RadarChartConfig
      chart = (
        <RadarChart data={radarConfig.data} margin={margin}>
          <PolarGrid />
          <PolarAngleAxis dataKey={radarConfig.xKey} />
          <PolarRadiusAxis />
          <Radar
            name={radarConfig.seriesName}
            dataKey={radarConfig.yKey}
            stroke={radarConfig.color}
            fill={radarConfig.color}
            fillOpacity={0.6}
          />
          {showLegend && <Legend />}
        </RadarChart>
      )
      break
    }

    case 'radialBar': {
      const radialConfig = chartConfig as unknown as RadialBarChartConfig
      chart = (
        <RadialBarChart
          data={radialConfig.data}
          innerRadius={radialConfig.innerRadius}
          outerRadius={radialConfig.outerRadius}
          margin={margin}
        >
          <RadialBar dataKey={radialConfig.valueKey} background />
          <Tooltip formatter={(value: any) => value.toLocaleString()} />
          {showLegend && <Legend />}
        </RadialBarChart>
      )
      break
    }

    case 'sankey': {
      const sankeyConfig = chartConfig as unknown as SankeyChartConfig
      chart = (
        <Sankey
          data={{
            nodes: sankeyConfig.nodes,
            links: sankeyConfig.links,
          }}
          margin={margin}
          link={{ stroke: '#77c878' }}
        />
      )
      break
    }

    default:
      chart = <div>Unsupported chart type</div>
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      {chart}
    </ResponsiveContainer>
  )
}

export default RechartsChart
